/**
 * Builders URLs doc / connect à partir du cache `GET /api/auth/config`.
 */

import { getCachedAuthConfig } from "@/lib/api/auth-config";

export function getSidoniDocRootUrl(): string {
  return getCachedAuthConfig().externalUrls.doc;
}

/** Base `…/docs/` pour concaténer un `nom` document. */
export function getSidoniDocDocsBase(): string {
  const root = getSidoniDocRootUrl();
  return root.endsWith("/") ? `${root}docs/` : `${root}/docs/`;
}

export function getConnectAccountUrl(): string {
  return getCachedAuthConfig().externalUrls.connectAccount;
}

/** Path relatif à la racine doc (ex. `docs/charte-ia`). */
export function buildSidoniDocHref(pathFromDocRoot: string): string {
  return new URL(
    pathFromDocRoot.replace(/^\/+/, ""),
    getSidoniDocRootUrl()
  ).toString();
}

/**
 * Force `auth=true` sur une URL doc Sidoni (SSO plateforme).
 * Idempotent si `auth` est déjà présent.
 */
export function withSidoniDocAuth(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  try {
    const parsed = new URL(trimmed);
    parsed.searchParams.set("auth", "true");
    return parsed.toString();
  } catch {
    if (/(?:^|[?&])auth=/.test(trimmed)) return trimmed;
    return trimmed.includes("?")
      ? `${trimmed}&auth=true`
      : `${trimmed}?auth=true`;
  }
}

/** URL document Sidoni à partir du `nom` (même convention que le chat). */
export function buildSidoniDocumentUrl(docNom: string): string {
  return withSidoniDocAuth(`${getSidoniDocDocsBase()}${docNom.trim()}`);
}

/**
 * URL pièce jointe Sidoni : `{docsBase}{docNom}/attachments/{attachmentNom}`.
 */
export function buildSidoniAttachmentUrl(
  docNom: string,
  attachmentNom: string
): string {
  const base = getSidoniDocDocsBase().replace(/\/$/, "");
  return withSidoniDocAuth(
    `${base}/${docNom.trim()}/attachments/${attachmentNom.trim()}`
  );
}

function ensureTrailingSlash(url: string): string {
  return url.endsWith("/") ? url : `${url}/`;
}

function isSidoniDocHost(hostname: string): boolean {
  return /^doc(-vite)?\./i.test(hostname);
}

/**
 * Réécrit une URL de ressource Sidoni (document, PJ, outil) sur l'origine doc
 * du client — même logique que la reconstruction des liens document.
 * Chemins relatifs résolus contre la racine doc ; hôtes `doc*` réécrits.
 * Les URLs doc reçoivent `auth=true` (SSO plateforme).
 */
export function resolveSidoniDocResourceUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;

  const docRoot = ensureTrailingSlash(getSidoniDocRootUrl());

  try {
    if (!/^https?:\/\//i.test(trimmed)) {
      return withSidoniDocAuth(
        new URL(trimmed.replace(/^\/+/, ""), docRoot).toString()
      );
    }

    const incoming = new URL(trimmed);
    if (!isSidoniDocHost(incoming.hostname)) {
      return trimmed;
    }

    return withSidoniDocAuth(
      new URL(
        `${incoming.pathname}${incoming.search}${incoming.hash}`,
        docRoot
      ).toString()
    );
  } catch {
    return trimmed;
  }
}
